import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import {
   ChangePmsRiskResolutionRequest,
   PmsRiskResolution,
   PmsRiskResolutionChangeResponse
} from '../../domain/pms-risk-resolution';
import { PmsRiskResolutionUtils } from '../../util/pms-risk-resolution-utils';
import { TaskService } from '../task.service';

export type HtmlReportRiskResolutionLoadStatus =
   | 'IDLE'
   | 'LOADING'
   | 'LOADED'
   | 'ERROR';

export interface HtmlReportRiskResolutionStoreState {
   status: HtmlReportRiskResolutionLoadStatus;
   resolutions: PmsRiskResolution[];
   errorStatus: number | null;
}

export type HtmlReportRiskResolutionMutationStatus =
   | 'IDLE'
   | 'SAVING'
   | 'SUCCESS'
   | 'ERROR';

export interface HtmlReportRiskResolutionMutationState {
   status: HtmlReportRiskResolutionMutationStatus;
   errorStatus: number | null;
   errorMessage: string | null;
   response: PmsRiskResolutionChangeResponse | null;
}

const INITIAL_STATE: HtmlReportRiskResolutionStoreState = {
   status: 'IDLE',
   resolutions: [],
   errorStatus: null
};

const INITIAL_MUTATION_STATE: HtmlReportRiskResolutionMutationState = {
   status: 'IDLE',
   errorStatus: null,
   errorMessage: null,
   response: null
};

@Injectable({
   providedIn: 'root'
})
export class HtmlReportRiskResolutionStoreService {
   private readonly stateSubject =
      new BehaviorSubject<HtmlReportRiskResolutionStoreState>(INITIAL_STATE);

   readonly state$ = this.stateSubject.asObservable();

   private readonly mutationStateSubject =
      new BehaviorSubject<HtmlReportRiskResolutionMutationState>(
         INITIAL_MUTATION_STATE
      );

   readonly mutationState$ = this.mutationStateSubject.asObservable();

   private activeTaskInstanceId: number | null = null;
   private activePmsDataId: number | null = null;

   /**
    * Only suppress duplicate requests while the same report is actively loading.
    * A successfully loaded report is deliberately not cached indefinitely so a
    * later visit retrieves fresh auditor resolution state.
    */
   private loadingKey: string | null = null;

   /**
    * Prevents a slower response for a previous task/report from replacing
    * state belonging to the currently displayed report.
    */
   private requestVersion = 0;

   /**
    * Prevents a mutation response belonging to an old report/context from
    * changing the current report's state.
    */
   private mutationVersion = 0;

   constructor(private taskService: TaskService) {}

   load(taskInstanceId: number, pmsDataId: number): void {
      if (taskInstanceId <= 0 || pmsDataId <= 0) {
         this.reset();
         return;
      }

      const contextChanged =
         this.activeTaskInstanceId !== taskInstanceId ||
         this.activePmsDataId !== pmsDataId;

      if (contextChanged) {
         this.resetMutation();
      }

      const key = this.buildKey(taskInstanceId, pmsDataId);

      if (this.loadingKey === key) {
         return;
      }

      if (!contextChanged && this.mutationStateSubject.value.status === 'SAVING') {
         return;
      }

      const requestVersion = ++this.requestVersion;

      this.activeTaskInstanceId = taskInstanceId;
      this.activePmsDataId = pmsDataId;
      this.loadingKey = key;

      this.stateSubject.next({
         status: 'LOADING',
         resolutions: [],
         errorStatus: null
      });

      this.taskService.getPmsRiskResolutions(taskInstanceId, pmsDataId).subscribe({
         next: (resolutions) => {
            if (requestVersion !== this.requestVersion) {
               return;
            }

            this.loadingKey = null;

            this.stateSubject.next({
               status: 'LOADED',
               resolutions: resolutions ?? [],
               errorStatus: null
            });
         },
         error: (error: HttpErrorResponse) => {
            if (requestVersion !== this.requestVersion) {
               return;
            }

            this.loadingKey = null;

            this.stateSubject.next({
               status: 'ERROR',
               resolutions: [],
               errorStatus: error.status
            });
         }
      });
   }

   change(request: ChangePmsRiskResolutionRequest): void {
      const taskInstanceId = this.activeTaskInstanceId;
      const pmsDataId = this.activePmsDataId;

      if (
         taskInstanceId === null ||
         pmsDataId === null ||
         pmsDataId !== request.pmsDataId ||
         this.stateSubject.value.status !== 'LOADED'
      ) {
         this.mutationStateSubject.next({
            status: 'ERROR',
            errorStatus: null,
            errorMessage: 'Risk resolution state is not ready.',
            response: null
         });
         return;
      }

      if (this.mutationStateSubject.value.status === 'SAVING') {
         return;
      }

      const mutationVersion = ++this.mutationVersion;

      this.mutationStateSubject.next({
         status: 'SAVING',
         errorStatus: null,
         errorMessage: null,
         response: null
      });

      this.taskService.changePmsRiskResolution(taskInstanceId, request).subscribe({
         next: (response) => {
            if (!this.isCurrentMutation(mutationVersion, taskInstanceId, pmsDataId)) {
               return;
            }

            const currentState = this.stateSubject.value;

            if (currentState.status === 'LOADED') {
               this.stateSubject.next({
                  ...currentState,
                  resolutions: PmsRiskResolutionUtils.merge(
                     currentState.resolutions,
                     response.resolutions
                  )
               });
            }

            this.mutationStateSubject.next({
               status: 'SUCCESS',
               errorStatus: null,
               errorMessage: null,
               response
            });

            if (currentState.status !== 'LOADED') {
               this.refresh();
            }
         },
         error: (error: HttpErrorResponse) => {
            if (!this.isCurrentMutation(mutationVersion, taskInstanceId, pmsDataId)) {
               return;
            }

            this.mutationStateSubject.next({
               status: 'ERROR',
               errorStatus: error.status,
               errorMessage: this.getErrorMessage(error),
               response: null
            });

            /**
             * A conflict means the browser's resolution state was stale.
             * Reload only in this case so the user sees the current
             * persisted state before trying another action.
             */
            if (error.status === 409) {
               this.refresh();
            }
         }
      });
   }

   refresh(): void {
      if (this.activeTaskInstanceId === null || this.activePmsDataId === null) {
         return;
      }

      this.load(this.activeTaskInstanceId, this.activePmsDataId);
   }

   resetMutation(): void {
      ++this.mutationVersion;
      this.mutationStateSubject.next(INITIAL_MUTATION_STATE);
   }

   reset(): void {
      ++this.requestVersion;

      this.activeTaskInstanceId = null;
      this.activePmsDataId = null;
      this.loadingKey = null;

      this.stateSubject.next(INITIAL_STATE);
      this.resetMutation();
   }

   private isCurrentMutation(
      mutationVersion: number,
      taskInstanceId: number,
      pmsDataId: number
   ): boolean {
      return (
         mutationVersion === this.mutationVersion &&
         taskInstanceId === this.activeTaskInstanceId &&
         pmsDataId === this.activePmsDataId
      );
   }

   private getErrorMessage(error: HttpErrorResponse): string | null {
      const message = error.error?.message;

      return typeof message === 'string' && message.trim() ? message.trim() : null;
   }

   private buildKey(taskInstanceId: number, pmsDataId: number): string {
      return `${taskInstanceId}:${pmsDataId}`;
   }
}
